import {
  Box,
  Button,
  Chip,
  Grid,
  makeStyles,
  Typography,
} from "@material-ui/core";
import { Close } from "@material-ui/icons";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { startOfDay } from "date-fns";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFormCombos } from "../../../../hooks/useFormCombos";
import { getCommittees } from "../../../../services/committees.service";
import { getCompaniesByUser } from "../../../../services/companies.service";
import { getMembersByName } from "../../../../services/formCombos.service.js";
import { getInputDateFormats } from "../../../../utils/get-date-time-locale";
import { Select } from "../../../Common/Select";
import Textarea from "../../../Common/Textarea";
import {
  ButtonsContainer,
  Content,
  FieldsContainer,
  HeaderContainer,
  OpenedInputDescription,
} from "./styles";

const useStyles = makeStyles((theme) => ({
  button: {
    fontSize: "1rem",
    fontWeight: "bold",
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.primary.contrastText,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  buttonOutlined: {
    border: "2px solid",
    fontSize: "1rem",
    fontWeight: "bold",
    color: theme.palette.grey.main,
    textTransform: "capitalize",
    minWidth: 180,
    minHeight: 50,
  },
  chip: {
    margin: 2,
  },
}));

function FilterDrawer({ onClose, t, filters, setFilters, actionShowSpinner }) {
  const classes = useStyles();
  const { i18n } = useTranslation();

  const [committeesOptions, setCommitteesOptions] = useState([]);

  const [presenterInputValue, setPresenterInputValue] = useState("");
  const [presenters, setPresenters] = useState([]);
  const [selectedCompanies, setSelectedCompanies] = useState(filters.companies);
  const [selectedPresenters, setSelectedPresenters] = useState(
    filters.presenters
  );
  const [requesterInputValue, setRequesterInputValue] = useState("");
  const [requesters, setRequesters] = useState([]);
  const [selectedRequester, setSelectedRequester] = useState(filters.requestId);
  const [directory, setDirectory] = useState(filters.directorship);
  const [slotType, setSlotType] = useState(filters.kind);
  const [budgeted, setBudgeted] = useState(filters.budgeted);
  const [modality, setModality] = useState(filters.modalityId);
  const [committees, setCommittees] = useState(filters.committees);
  const [meetStartDate, setMeetStartDate] = useState(filters.startDate);
  const [meetEndDate, setMeetEndDate] = useState(filters.endDate);
  const [selectedStatus, setSelectedStatus] = useState(filters.status);
  const [statusMaterial, setStatusMaterial] = useState(filters.statusMaterial);
  const [openedField, setOpenedField] = useState(filters.keyWord);
  const [companiesOptions, setCompaniesOptions] = useState([]);

  const {
    modalityList,
    getAllModalityByLanguageId,
    sanitizeForTable,
    getDirectors,
    directorsList,
    sanitizeDirectorsForSelect,
    getTypeSlot,
    typeSlot,
    getBudgetedOptions,
    budgetedOptions,
    getStatusSlot,
    statusSlot,
    getStatusMaterial: getStatusMaterials,
    statusMaterial: statusMaterials,
  } = useFormCombos();

  useEffect(() => {
    const language = localStorage.getItem("i18nextLng");

    const loadCommitteeOptions = async () => {
      const response = await getCommittees({
        showDisabled: true,
        pageSize: 999999
      });
      
      setCommitteesOptions(
        response.data.items
      );
    };

    const loadCompaniesOptions = async () => {
      const companies = await getCompaniesByUser();

      setCompaniesOptions(companies);
    };

    actionShowSpinner(true);

    loadCommitteeOptions();
    loadCompaniesOptions();

    getDirectors(language);
    getTypeSlot(language);
    getBudgetedOptions(language);
    getAllModalityByLanguageId(language, false);
    getStatusSlot(language);
    getStatusMaterials(language);

    actionShowSpinner(false);
  }, []);

  useEffect(() => {
    setSelectedCompanies(filters?.companies);
    setSelectedPresenters(filters?.presenters);
    setSelectedRequester(filters?.requestId);
    setDirectory(filters?.directorship);
    setSlotType(filters?.kind);
    setBudgeted(filters?.budgeted);
    setModality(filters?.modalityId);
    setCommittees(filters?.committees);
    setMeetStartDate(filters?.startDate);
    setMeetEndDate(filters?.endDate);
    setSelectedStatus(filters?.status);
    setStatusMaterial(filters?.statusMaterial);
    setOpenedField(filters?.keyWord);
  }, [filters]);

  useEffect(() => {
    async function load() {
      if (requesterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", requesterInputValue);

        if (res.error?.length > 0) {
          setRequesters([]);
        }

        setRequesters(res.data);
      } else {
        setRequesters([]);
      }

      if (presenterInputValue?.length >= 3) {
        const res = await getMembersByName("pt", presenterInputValue);

        if (res.error?.length > 0) {
          setPresenters([]);
        }

        setPresenters(res.data);
      } else {
        setPresenters([]);
      }
    }

    load();
  }, [requesterInputValue, presenterInputValue]);

  const handleApplyFilters = () => {
    setFilters({
      ...(selectedStatus && { status: selectedStatus }),
      ...(selectedCompanies && { companies: selectedCompanies }),
      ...(selectedPresenters && { presenters: selectedPresenters }),
      ...(selectedRequester && { requestId: selectedRequester }),
      ...(directory && { directorship: directory }),
      ...(slotType && { kind: slotType }),
      ...(budgeted && { budgeted: budgeted }),
      ...(modality && { modalityId: modality }),
      ...(committees && { committees: committees }),
      ...(meetStartDate ? { startDate: meetStartDate } : { startDate: null }),
      ...(meetEndDate ? { endDate: meetEndDate } : { endDate: null }),
      ...(statusMaterial && { statusMaterial }),
      ...(openedField && { keyWord: openedField }),
    });

    onClose();
  };

  const handleCleanFilters = () => {
    setFilters({
      status: [],
      statusMaterial: undefined,
      responsible: undefined,
      requestId: undefined,
      directorship: undefined,
      kind: undefined,
      budgeted: undefined,
      modalityId: undefined,
      committees: [],
      presenters: [],
      companies: [],
      finalElevation: undefined,
      startDate: null,
      endDate: null,
      keyWord: undefined,
    });

    onClose();
  };

  return (
    <Box sx={{ width: 460, flex: 1, display: "flex" }} role="presentation">
      <Content>
        <HeaderContainer>
          <Typography variant="h4">{t("forms.filters")}</Typography>

          <Close
            onClick={() => {
              onClose();
            }}
          />
        </HeaderContainer>

        <FieldsContainer>
          <Grid container spacing={3}>
            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="requester"
                options={requesters}
                value={selectedRequester}
                onInputChange={(_event, newInputValue, _prop) => {
                  setRequesterInputValue(newInputValue);
                }}
                inputValue={requesterInputValue}
                label={t("forms.minutes.actions.requesterName")}
                setValue={(value) => {
                  setSelectedRequester(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="presenters"
                options={presenters}
                value={selectedPresenters}
                label={t("slots.presenters")}
                setValue={(values) => {
                  setSelectedPresenters(values);
                }}
                onInputChange={(_event, newInputValue, _prop) => {
                  setPresenterInputValue(newInputValue);
                }}
                inputValue={presenterInputValue}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setSelectedPresenters(
                          selectedPresenters.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setSelectedPresenters(
                          selectedPresenters.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.isExternal ? `${option.name} (Externo)` : option.name}
                      style={option.isExternal ? { borderStyle: "dashed" } : {}}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="directories"
                options={sanitizeDirectorsForSelect(directorsList)}
                value={directory}
                label={t("slots.board")}
                setValue={(value) => {
                  setDirectory(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="companies"
                options={
                  companiesOptions?.map((item) => ({
                    id: item.companyId,
                    name: `${item.companyName} - ${item.companyAbbreviation}`,
                  })) ?? []
                }
                getOptionSelected={(option, value) => option.id === value.id}
                value={selectedCompanies}
                label={t("slots.companies")}
                setValue={(values) => {
                  setSelectedCompanies(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setSelectedCompanies(
                          selectedCompanies.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="type"
                options={typeSlot}
                value={slotType}
                label={t("slots.type")}
                setValue={(value) => {
                  setSlotType(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="budgeted"
                options={budgetedOptions}
                value={budgeted}
                label={t("slots.budget.title")}
                setValue={(value) => {
                  setBudgeted(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="modality"
                options={sanitizeForTable(modalityList)}
                value={modality}
                label={t("slots.modality")}
                setValue={(value) => {
                  setModality(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="committees"
                options={committeesOptions ? committeesOptions : []}
                value={committees}
                label={t("committees")}
                setValue={(values) => {
                  setCommittees(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setCommittees(
                          committees.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setCommittees(
                          committees.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("forms.startDateLabel")}
                value={meetStartDate}
                onChange={(date) =>
                  setMeetStartDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={6} xs={6}>
              <KeyboardDatePicker
                autoOk
                disableToolbar
                variant="inline"
                format={getInputDateFormats(i18n.language)}
                id="createdIn"
                fullWidth
                label={t("forms.endDateLabel")}
                value={meetEndDate}
                onChange={(date) =>
                  setMeetEndDate(date ? startOfDay(date) : null)
                }
                KeyboardButtonProps={{
                  "aria-label": "change date",
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                fullWidth
                id="status"
                options={statusSlot}
                value={selectedStatus}
                label={t("slots.statusSlot")}
                setValue={(values) => {
                  setSelectedStatus(values);
                }}
                multiple={true}
                renderTags={(selectedValues) => {
                  return selectedValues.map((option) => (
                    <Chip
                      className={classes.chip}
                      key={option.id}
                      onDelete={() => {
                        setSelectedStatus(
                          selectedStatus.filter((v) => v.id !== option.id)
                        );
                      }}
                      onClick={() => {
                        setSelectedStatus(
                          selectedStatus.filter((v) => v.id !== option.id)
                        );
                      }}
                      variant="outlined"
                      label={option.name}
                    />
                  ));
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Select
                id="statusMaterial"
                options={statusMaterials}
                value={statusMaterial}
                label={t("slots.statusMaterial")}
                setValue={(value) => {
                  setStatusMaterial(value);
                }}
              />
            </Grid>

            <Grid item lg={12} xs={12}>
              <Textarea
                name="freeField"
                id="freeField"
                onChange={(e) => setOpenedField(e.target.value)}
                value={openedField}
                style={{ width: "100%" }}
                label={t("forms.openedField")}
              />
              <OpenedInputDescription>
                {t("slots.keywordFieldDescription")}
              </OpenedInputDescription>
            </Grid>
          </Grid>
        </FieldsContainer>

        <ButtonsContainer>
          <Button
            variant="contained"
            onClick={handleCleanFilters}
            className={classes.buttonOutlined}
          >
            {t("forms.cleanFilters")}
          </Button>

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            className={classes.button}
          >
            {t("forms.applyFilters")}
          </Button>
        </ButtonsContainer>
      </Content>
    </Box>
  );
}

export default FilterDrawer;
